/**
 * EnquiryModal — lightweight email-style enquiry form used as a
 * fallback when a buyer doesn't want to spin up a chat session.
 * Prefills the current user's name/email when signed in and posts to
 * the existing `POST /api/contact` endpoint with the property URL
 * embedded in the message so the ops team can forward.
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, CheckCircle2, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EnquiryModal = ({ open, onOpenChange, property }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name:    user?.name  || '',
    email:   user?.email || '',
    phone:   '',
    message:
      `Hi,\n\nI'm interested in ${property?.title || 'this property'} (${property?.location?.area || ''}). ` +
      `Please could you send over more info and let me know your next viewing times?\n\nThanks`,
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      setError('Please add your name, email, and a short message (10 chars+).');
      return;
    }
    setBusy(true);
    try {
      const pid  = property?._id || property?.id;
      const url  = pid ? `${window.location.origin}/property/${pid}` : window.location.href;
      const body =
        `${form.message.trim()}\n\n` +
        `--\nProperty: ${property?.title || '(unknown)'} \n` +
        `URL: ${url}\n` +
        `Agent: ${property?.agent?.name || '(unknown)'}\n` +
        (form.phone.trim() ? `Buyer phone: ${form.phone.trim()}\n` : '');
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name:     form.name.trim().slice(0, 120),
        email:    form.email.trim(),
        subject:  'listing-issue',   // routes to ops team → forwarded to agent
        audience: 'buyer',
        message:  body.slice(0, 4000),
      });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send. Try again in a moment.');
    } finally { setBusy(false); }
  };

  const mailtoFallback = () => {
    const to  = property?.agent?.email || '';
    const sub = `Enquiry: ${property?.title || 'Property'}`;
    const pid = property?._id || property?.id;
    const url = pid ? `${window.location.origin}/property/${pid}` : window.location.href;
    const body = encodeURIComponent(
      `${form.message}\n\n---\nProperty: ${property?.title || ''}\n${url}`
    );
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(sub)}&body=${body}`;
  };

  const close = () => {
    onOpenChange(false);
    // Reset after a beat so the next open feels fresh
    setTimeout(() => { setSent(false); setError(null); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="enquiry-modal"
      >
        <DialogHeader>
          <DialogTitle>Send an enquiry</DialogTitle>
          <DialogDescription>
            {sent
              ? 'Message received — the agent will be in touch within one working day.'
              : `Ask about viewings, floorplans or anything else — the agent will reply to your email.`}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center space-y-3" data-testid="enquiry-sent">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <p className="text-sm text-gray-700">
              We&apos;ve got your message and forwarded it to <strong>{property?.agent?.name || 'the agent'}</strong>.
            </p>
            <Button onClick={close} className="bg-[#7042cc] hover:bg-[#5b35a8]" data-testid="enquiry-close-btn">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="enq-name" className="text-xs">Your name</Label>
                <Input id="enq-name" value={form.name} onChange={set('name')} data-testid="enquiry-name" />
              </div>
              <div>
                <Label htmlFor="enq-email" className="text-xs">Email</Label>
                <Input id="enq-email" type="email" value={form.email} onChange={set('email')} data-testid="enquiry-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="enq-phone" className="text-xs">Phone <span className="text-gray-400">(optional)</span></Label>
              <Input id="enq-phone" type="tel" value={form.phone} onChange={set('phone')} data-testid="enquiry-phone" />
            </div>
            <div>
              <Label htmlFor="enq-message" className="text-xs">Message</Label>
              <Textarea
                id="enq-message"
                rows={5}
                value={form.message}
                onChange={set('message')}
                data-testid="enquiry-message"
              />
            </div>

            {error && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="enquiry-error">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between pt-2">
              {property?.agent?.email && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={mailtoFallback}
                  className="text-xs text-gray-500 hover:text-[#7042cc]"
                  data-testid="enquiry-mailto"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Open in email app
                </Button>
              )}
              <Button
                type="button"
                onClick={submit}
                disabled={busy}
                className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                data-testid="enquiry-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        : <Mail className="h-4 w-4 mr-1.5" />}
                Send enquiry
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryModal;
